/**
 * CompanionIdeas — "Ideas for you", the short list under the family's
 * composer on the parent home (ui-ux-guideline §2.9 item 2, 2026-09-18). The
 * family-side twin of `WilliamsIdeas`: same heading, same row, but the list
 * is the caller's (`buildParentIdeas` — the family's generated suggestions,
 * or the static chips until they land).
 *
 * Host-side on purpose, like its twin: it needs nothing from the chat core.
 * #2265 (Victor 2026-10-06) made it the ONLY suggestion surface in the
 * family's conversation: the per-turn reply chips (the core's
 * `SuggestionChips`, #1308) are no longer drawn, and this list shows at the
 * START of a conversation only — `CompanionThread` renders it while the
 * thread is empty and nobody has sent on it, `ComposerHero` hides it the
 * moment the parent types, and no memorial board passes it a list (#644).
 */
import { useTranslation } from 'react-i18next';
import { MONO_LABEL_TYPE } from '@/components/editorial';
import type { ParentIdea } from '@/lib/parent-home-ideas';
import { cn } from '@/lib/utils';

interface CompanionIdeasProps {
  ideas: ParentIdea[];
  onPick: (idea: ParentIdea) => void;
  /** Renders nothing. The draft signal lives with the composer. */
  hidden?: boolean;
  disabled?: boolean;
  className?: string;
}

export function CompanionIdeas({ ideas, onPick, hidden = false, disabled = false, className }: CompanionIdeasProps) {
  const { t } = useTranslation('companion');
  if (hidden || ideas.length === 0) return null;

  // #2109 R3-B2: a picked idea sends its label, in the page's language. The
  // `(?)` marker beside the heading names that choice (never inside the h2).
  return (
    <section aria-labelledby="companion-ideas-heading" className={cn('space-y-1', className)}>
      <div className="flex items-center gap-1 px-3">
        <h2 id="companion-ideas-heading" className={cn(MONO_LABEL_TYPE, 'm-0 text-stevens-ink-3')}>
          {t('ideas.heading')}
        </h2>
      </div>
      <ul className="m-0 list-none p-0">
        {ideas.map((idea) => {
          const Icon = idea.icon;
          return (
            <li key={idea.id}>
              <button
                type="button"
                onClick={() => onPick(idea)}
                disabled={disabled}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body text-stevens-ink-2 transition-colors',
                  'hover:bg-stevens-bg-2 hover:text-stevens-ink',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
                )}
              >
                <Icon size={16} aria-hidden="true" className="shrink-0 text-stevens-ink-3" />
                <span>{idea.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
